import { BaseSyntheticEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getUnitType } from 'utils/lead/leadUtils';
import { FormContextParts } from 'components/forms/form/Form.types';
import { Property } from 'models/Properties';
import { ICalType } from 'models/ICal';
import useNotify from 'hooks/useNotify';
import { CalendarImportFormValues } from './CalendarImportForm.schema';
import useCreateIcal from './useCreateIcal';
import { ICALS_BASE_QUERY_KEY } from './useFetchIcals';

export const formDefaultValues = {
  synchronizeAfterCreation: true,
  type: '',
  property: '',
  url: '',
  name: '',
};

export const useCalendarImportFormSubmit = ({
  allProperties,
}: {
  allProperties: Property[] | undefined;
}) => {
  const queryClient = useQueryClient();
  const { notifySuccess } = useNotify();
  const { createIcal } = useCreateIcal();
  const { t } = useTranslation();

  const onSubmit = async (
    data: CalendarImportFormValues,
    _: BaseSyntheticEvent | undefined,
    { reset }: FormContextParts<CalendarImportFormValues>,
  ) => {
    const { property, type, url, synchronizeAfterCreation, name } = data;
    const unitType = getUnitType(allProperties || [], property);

    const payload = {
      url,
      type,
      ...(name ? { name } : {}),
      ...(unitType
        ? { propertyUid: unitType.uid, unitUid: property }
        : { propertyUid: property }),
    };

    if (type !== ICalType.DEFAULT) {
      delete payload.name;
    }

    await createIcal({ payload, synchronizeAfterCreation });

    // @ts-expect-error "type" field needs to be empty string
    reset({ ...formDefaultValues, property });
    notifySuccess(
      t('pageAutomations.calendarTools.calendarImportSection.success'),
    );
    queryClient.invalidateQueries({
      queryKey: [ICALS_BASE_QUERY_KEY],
    });
  };

  return onSubmit;
};
